import React, { forwardRef, useEffect, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ThreeDot } from "react-loading-indicators";
import i18n from "../../lib/i18n";

const AdsDetailsPop = forwardRef(({ data, onClose }, ref) => {
  const allPhotos = [data?.main_photo, ...(data?.photos?.map((e) => e.photo) ?? [])].filter(Boolean);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingPhoto, setLoadingPhoto] = useState(true);
  const t = i18n.t.bind(i18n);

  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % allPhotos.length;
    setLoadingPhoto(true);
    setCurrentIndex(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? allPhotos.length - 1 : currentIndex - 1;
    setLoadingPhoto(true);
    setCurrentIndex(prevIndex);
  };

  useEffect(() => {
    if (data && Object.keys(data || {}).length > 0) {
      setLoading(false);
      setCurrentIndex(0);
    }
  }, [data]);

  const handleImageLoad = () => {
    setLoadingPhoto(false);
  };

  const hasMultiplePhotos = allPhotos.length > 1;
  const isEmpty = !data || Object.keys(data || {}).length === 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div
        className="bg-thirdly rounded-2xl shadow-lg lg:w-[55%] md:w-[75%] sm:w-[90%] w-full max-w-4xl p-6 relative overflow-y-auto max-h-[90vh] custom-scrollbar text-white"
        ref={ref}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-xl transition-colors"
        >
          ✕
        </button>

        {loading ? (
          <div className="flex items-center justify-center h-96 w-full">
            <ThreeDot variant="bounce" color="#D06421" size="large" text="" textColor="" />
          </div>
        ) : !isEmpty && (
          <>
            {/* Title & Basic Identifiers */}
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold mb-1">
                {data?.title || t("order.no_title", "No Title Provided")}
              </h2>
              <p className="text-sm text-gray-400">
                {t("order.ad_id", "Ad ID")}: #{data?.id}
              </p>
            </div>

            {/* Image Carousel Slider */}
            <div className="relative flex justify-center items-center w-full h-64 sm:h-80 bg-black/20 rounded-xl overflow-hidden mb-6">
              {loadingPhoto && allPhotos.length > 0 && (
                <div className="absolute z-10 bg-black/60 text-white px-4 py-2 rounded">
                  {t("loading", "Loading...")}
                </div>
              )}
              {allPhotos.length > 0 ? (
                <img
                  key={currentIndex}
                  src={allPhotos[currentIndex]}
                  alt="Ad Visual"
                  onLoad={handleImageLoad}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-gray-400">{t("order.no_images", "No images available")}</div>
              )}

              {hasMultiplePhotos && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute top-1/2 -translate-y-1/2 left-2 bg-secondary p-1.5 rounded-full text-white hover:bg-opacity-80 transition"
                  >
                    <IoIosArrowBack size={20} />
                  </button>

                  <button
                    onClick={nextSlide}
                    className="absolute top-1/2 -translate-y-1/2 right-2 bg-secondary p-1.5 rounded-full text-white hover:bg-opacity-80 transition"
                  >
                    <IoIosArrowForward size={20} />
                  </button>
                </>
              )}
            </div>

            {/* Details Layout Grid */}
            <div className="space-y-6">
              
              {/* Section 1: Ad Core Properties & Classification */}
              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-secondary mb-3">
                  {t("order.ads_information")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p className="col-span-1 md:col-span-2">
                    <span className="text-gray-400">{t("order.description")}:</span> {data?.description || "—"}
                  </p>
                  <p><span className="text-gray-400">{t("order.category_name", "Category")}:</span> {data?.category_name || "—"}</p>
                  <p><span className="text-gray-400">{t("order.sub_category_name", "Sub-category")}:</span> {data?.sub_category_name || "—"}</p>
                  <p><span className="text-gray-400">{t("order.price")}:</span> {data?.price} {data?.currency}</p>
                  <p><span className="text-gray-400">{t("order.publish_at")}:</span> {data?.publish_at || "—"}</p>
                  <p>
                    <span className="text-gray-400">{t("order.is_boosted", "Boosted")}:</span>{" "}
                    <span className={`px-1.5 py-0.5 text-xs rounded font-bold ${data?.is_boosted ? "bg-amber-500 text-black" : "bg-gray-600"}`}>
                      {data?.is_boosted ? t("general.yes", "Yes") : t("general.no", "No")}
                    </span>
                  </p>
                  <p><span className="text-gray-400">{t("order.count_days")}:</span> {data?.count_days || "—"}</p>
                  <p><span className="text-gray-400">{t("order.boost_start_time")}:</span> {data?.boost_start_time || "—"}</p>
                  <p><span className="text-gray-400">{t("order.boost_end_time")}:</span> {data?.boost_end_time || "—"}</p>
                  <p><span className="text-gray-400">{t("order.likes", "Likes")}:</span> {data?.likedCount || 0}</p>
                </div>
              </div>

              {/* Section 2: Status & Location */}
              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-secondary mb-3">
                  {t("order.status_location", "Status & Location")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p>
                    <span className="text-gray-400">{t("order.order_status")}:</span>{" "}
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        data?.order === "CONFIRM"
                          ? "bg-green-600 text-white"
                          : data?.order === "SoldRemoved"
                            ? "bg-blue-600 text-white"
                            : data?.order === "REJECT"
                              ? "bg-red-500 text-white"
                              : "bg-gray-600 text-white"
                      }`}
                    >
                      {data?.order || "—"}
                    </span>
                  </p>
                  <p><span className="text-gray-400">{t("order.address")}:</span> {data?.address || "—"}</p>
                  {data?.latitude && data?.longitude && (
                    <p className="col-span-1 md:col-span-2">
                      <span className="text-gray-400">{t("order.coordinates", "Coordinates")}:</span> {data.latitude}, {data.longitude}
                    </p>
                  )}
                </div>
              </div>

              {/* Section 3: Dynamic Other Information (The "Perfect Layout" Cards) */}
              {data?.other_info && data.other_info.length > 0 && (
                <div className="border-b border-gray-700 pb-4">
                  <div className="p-4 bg-black/20 rounded-xl border border-gray-700/40 w-full">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-3">
                      {t("order.other_details", "Other Details")}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {data.other_info.map((info) => {
                        // Display clean booleans if value is true/false, else string
                        let displayValue = "—";
                        if (typeof info.value === "boolean") {
                          displayValue = info.value ? t("general.yes", "Yes") : t("general.no", "No");
                        } else if (info.value !== undefined && info.value !== null) {
                          displayValue = String(info.value);
                        }

                        return (
                          <div 
                            key={info.id || info.label} 
                            className="flex justify-between items-center p-2.5 bg-thirdly/40 rounded-lg border border-gray-800 hover:border-gray-700/60 transition-colors"
                          >
                            <span className="text-gray-400 font-medium">{info.label}</span>
                            <span className="font-semibold text-white max-w-[60%] truncate" title={displayValue}>
                              {displayValue}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Section 4: Publisher Information */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-3">
                  {t("order.user_information", "Publisher Information")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p><span className="text-gray-400">{t("order.full_name")}:</span> {data?.publisher_details?.full_name || "—"}</p>
                  <p><span className="text-gray-400">{t("order.username")}:</span> {data?.publisher_details?.user_name || "—"}</p>
                  <p><span className="text-gray-400">{t("order.phone_number", "Phone")}:</span> <span dir="ltr">{data?.publisher_details?.phone_number || "—"}</span></p>
                  <p><span className="text-gray-400">{t("order.email")}:</span> {data?.publisher_details?.email || "—"}</p>
                  <p><span className="text-gray-400">{t("order.type")}:</span> {data?.publisher_details?.type || "—"}</p>
                  <p><span className="text-gray-400">{t("order.address")}:</span> {data?.publisher_details?.address || "—"}</p>
                </div>
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default AdsDetailsPop;